import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn, Path, PathValue } from "react-hook-form";


type HistoryEntry<T> = {
  state: T;
  timestamp: number;
};


export interface EditorHistoryOptions {
  maxHistorySize?: number;
  debounceMs?: number;
}


export function useEditorHistory<T extends Record<string, unknown>>(
  form: UseFormReturn<T>,
  options: EditorHistoryOptions = {},
) {
  const { maxHistorySize = 50, debounceMs = 0 } = options;

  const [history, setHistory] = useState<HistoryEntry<T>[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const transactionRef = useRef<boolean>(false);
  const pendingChangesRef = useRef<T | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Initialize history with current state after a short delay to ensure template has loaded
  useEffect(() => {
    if (!hasInitializedRef.current && history.length === 0) {
      const timer = setTimeout(() => {
        const initialState = form.getValues();
        // Only initialize if the form has actual content (not just defaults)
        if (initialState && Object.keys(initialState).length > 0) {
          setHistory([{
            state: structuredClone(initialState),
            timestamp: Date.now(),
          }]);
          setCurrentIndex(0);
          hasInitializedRef.current = true;
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [form, history.length]);

  const startTransaction = useCallback(() => {
    transactionRef.current = true;
    pendingChangesRef.current = null;
  }, []);

  const commitTransaction = useCallback(() => {
    if (transactionRef.current && pendingChangesRef.current) {
      const newHistory = history.slice(0, currentIndex + 1);
      newHistory.push({
        state: pendingChangesRef.current,
        timestamp: Date.now(),
      });

      if (newHistory.length > maxHistorySize) {
        newHistory.shift();
      } else {
        setCurrentIndex(newHistory.length - 1);
      }

      setHistory(newHistory);
    }
    transactionRef.current = false;
    pendingChangesRef.current = null;
  }, [history, currentIndex, maxHistorySize]);

  // Track a change (during or outside transaction)
  const captureState = useCallback(() => {
    if (isRestoringRef.current) {
      return;
    }

    const currentState = form.getValues();

    const addToHistory = () => {
      if (transactionRef.current) {
        // Inside transaction: just update pending changes
        pendingChangesRef.current = currentState;
      } else {
        // Outside transaction: immediately save to history
        const newHistory = history.slice(0, currentIndex + 1);
        newHistory.push({
          state: structuredClone(currentState),
          timestamp: Date.now(),
        });

        if (newHistory.length > maxHistorySize) {
          newHistory.shift();
        } else {
          setCurrentIndex((prev) => prev + 1);
        }

        setHistory(newHistory);
      }
    };

    if (debounceMs > 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(addToHistory, debounceMs);
    } else {
      addToHistory();
    }
  }, [form, history, currentIndex, debounceMs, maxHistorySize]);

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      isRestoringRef.current = true;
      const newIndex = currentIndex - 1;
      const entry = history[newIndex];

      // Use setValue instead of reset to preserve dirty state
      Object.keys(entry.state).forEach((key) => {
        const path = key as Path<T>;
        form.setValue(path, entry.state[key] as PathValue<T, Path<T>>, { shouldDirty: true });
      });

      setCurrentIndex(newIndex);
      // Reset the flag after a short delay to allow form updates to propagate
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
    }
  }, [currentIndex, history, form]);

  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      isRestoringRef.current = true;
      const newIndex = currentIndex + 1;
      const entry = history[newIndex];

      // Use setValue instead of reset to preserve dirty state
      Object.keys(entry.state).forEach((key) => {
        const path = key as Path<T>;
        form.setValue(path, entry.state[key] as PathValue<T, Path<T>>, { shouldDirty: true });
      });

      setCurrentIndex(newIndex);
      // Reset the flag after a short delay to allow form updates to propagate
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
    }
  }, [currentIndex, history, form]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setCurrentIndex(-1);
    hasInitializedRef.current = false;
  }, []);

  return {
    undo,
    redo,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    startTransaction,
    commitTransaction,
    captureState,
    clearHistory,
    historySize: history.length,
  };
}
